import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formatMoney } from '@/components/admin/licence-status-chip';
import { DataTable, dataTableColumnHelper } from '@/components/data-table';
import { PageHeader } from '@/components/page-header';
import { StatusChip } from '@/components/status-chip';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  adminApi,
  adminKeys,
  type AdminActivation,
  type AdminDomainVerification,
  type AdminPayment,
  type AdminWebhookEvent,
} from '@/lib/api/admin';
import { messageForError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/utils';

const activationCol = dataTableColumnHelper<AdminActivation>();
const domainCol = dataTableColumnHelper<AdminDomainVerification>();
const webhookCol = dataTableColumnHelper<AdminWebhookEvent>();
const paymentCol = dataTableColumnHelper<AdminPayment>();

const activationColumns = [
  activationCol.accessor('organisationName', { header: 'Organisation' }),
  activationCol.accessor('domain', { header: 'Domain', cell: (i) => i.getValue() ?? '—' }),
  activationCol.accessor('environmentType', { header: 'Environment' }),
  activationCol.accessor('status', { header: 'Status' }),
  activationCol.accessor('consumedSlot', { header: 'Slot', cell: (i) => (i.getValue() ? 'Yes' : 'No') }),
  activationCol.accessor('activatedAt', { header: 'When', cell: (i) => formatDateTime(i.getValue()) }),
];
const domainColumns = [
  domainCol.accessor('hostname', { header: 'Hostname' }),
  domainCol.accessor('organisationName', { header: 'Organisation' }),
  domainCol.accessor('status', {
    header: 'Status',
    cell: (i) => (
      <StatusChip tone={i.getValue() === 'verified' ? 'active' : i.getValue() === 'failed' ? 'danger' : 'pending'}>
        {i.getValue()}
      </StatusChip>
    ),
  }),
  domainCol.accessor('attempts', { header: 'Attempts' }),
  domainCol.accessor('lastError', { header: 'Last error', cell: (i) => i.getValue() ?? '—' }),
];
const webhookColumns = [
  webhookCol.accessor('receivedAt', { header: 'Received', cell: (i) => formatDateTime(i.getValue()) }),
  webhookCol.accessor('provider', { header: 'Provider' }),
  webhookCol.accessor('type', { header: 'Type', cell: (i) => <span className="font-mono text-micro">{i.getValue()}</span> }),
  webhookCol.accessor('status', {
    header: 'Status',
    cell: (i) => (
      <StatusChip tone={i.getValue() === 'processed' ? 'active' : i.getValue() === 'failed' ? 'danger' : 'pending'}>
        {i.getValue()}
      </StatusChip>
    ),
  }),
  webhookCol.accessor('attempts', { header: 'Attempts' }),
  webhookCol.accessor('error', { header: 'Error', cell: (i) => i.getValue() ?? '—' }),
];
const paymentColumns = [
  paymentCol.accessor('createdAt', { header: 'When', cell: (i) => formatDateTime(i.getValue()) }),
  paymentCol.display({
    id: 'amount',
    header: 'Amount',
    cell: ({ row }) => formatMoney(row.original.amount, row.original.currency),
  }),
  paymentCol.accessor('status', {
    header: 'Status',
    cell: (i) => (
      <StatusChip tone={i.getValue() === 'succeeded' ? 'active' : i.getValue() === 'failed' ? 'danger' : 'pending'}>
        {i.getValue().replaceAll('_', ' ')}
      </StatusChip>
    ),
  }),
  paymentCol.accessor('failureMessage', { header: 'Failure', cell: (i) => i.getValue() ?? '—' }),
];

function HealthPanel() {
  const query = useQuery({ queryKey: adminKeys.health(), queryFn: adminApi.health, refetchInterval: 30_000 });
  if (query.isPending) return <p className="text-data text-ink-muted">Checking…</p>;
  if (query.isError) return <p className="text-data text-danger">{messageForError(query.error)}</p>;
  const { checks, queues, licenceSigning } = query.data;
  const ok = (value: boolean) => (
    <StatusChip tone={value ? 'active' : 'danger'}>{value ? 'Healthy' : 'Down'}</StatusChip>
  );
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Dependencies</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-data">
          <div className="flex items-center justify-between">Database {ok(checks.database)}</div>
          <div className="flex items-center justify-between">Redis {ok(checks.redis)}</div>
          <div className="flex items-center justify-between">
            Billing provider <span className="font-mono text-micro">{checks.billingProvider}</span>
          </div>
          <div className="flex items-center justify-between">
            Queue mode <span className="font-mono text-micro">{checks.queueMode}</span>
          </div>
          <div className="flex items-center justify-between">
            Licence signing
            <span className="flex items-center gap-2">
              <span className="font-mono text-micro">{licenceSigning.keyId}</span>
              {ok(licenceSigning.configured)}
            </span>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Queue backlog</CardTitle>
          <CardDescription>Waiting, delayed or active jobs per queue</CardDescription>
        </CardHeader>
        <CardContent>
          {queues.length === 0 ? (
            <p className="text-data text-ink-muted">All queues are empty.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-hairline">
              {queues.map((queue) => (
                <li key={queue.queue} className="flex justify-between py-2 text-data">
                  <span className="font-mono text-micro">{queue.queue}</span>
                  <span data-numeric>{queue.waiting}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FlagsPanel() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: adminKeys.flags(), queryFn: adminApi.featureFlags });
  const toggle = useMutation({
    mutationFn: (input: { key: string; enabled: boolean; rolloutPercent: number }) =>
      adminApi.setFeatureFlag(input.key, { enabled: input.enabled, rolloutPercent: input.rolloutPercent }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: adminKeys.flags() }),
    onError: (error) => toast.error(messageForError(error)),
  });
  const flags = query.data?.items ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Feature flags</CardTitle>
        <CardDescription>Platform-wide switches. Changing one needs the super administrator role.</CardDescription>
      </CardHeader>
      <CardContent>
        {flags.length === 0 ? (
          <p className="text-data text-ink-muted">No flags defined.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-hairline">
            {flags.map((flag) => (
              <li key={flag.key} className="flex items-center justify-between gap-4 py-3">
                <div className="flex flex-col">
                  <span className="font-mono text-micro">{flag.key}</span>
                  {flag.description ? <span className="text-data text-ink-muted">{flag.description}</span> : null}
                </div>
                <Switch
                  checked={flag.enabled}
                  aria-label={`Toggle ${flag.key}`}
                  onCheckedChange={(enabled) =>
                    toggle.mutate({ key: flag.key, enabled, rolloutPercent: flag.rolloutPercent })
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function OperationsPage() {
  const activations = useQuery({ queryKey: adminKeys.activations({}), queryFn: () => adminApi.activations({ perPage: 50 }) });
  const domains = useQuery({ queryKey: adminKeys.domains(), queryFn: adminApi.domainVerifications });
  const webhooks = useQuery({ queryKey: adminKeys.webhooks({}), queryFn: () => adminApi.webhookEvents({ perPage: 50 }) });
  const payments = useQuery({ queryKey: adminKeys.payments({}), queryFn: () => adminApi.payments({ perPage: 50 }) });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Platform"
        title="Operations"
        description="System health, activations, domain verification, billing webhooks and payments."
      />
      <Tabs defaultValue="health">
        <TabsList>
          <TabsTrigger value="health">Health</TabsTrigger>
          <TabsTrigger value="activations">Activations</TabsTrigger>
          <TabsTrigger value="domains">Domains</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="flags">Flags</TabsTrigger>
        </TabsList>
        <TabsContent value="health" className="pt-4">
          <HealthPanel />
        </TabsContent>
        <TabsContent value="activations" className="pt-4">
          <DataTable caption="Recent activations" columns={activationColumns} data={activations.data?.items ?? []} isLoading={activations.isPending} error={activations.isError ? messageForError(activations.error) : undefined} getRowId={(r) => r.id} />
        </TabsContent>
        <TabsContent value="domains" className="pt-4">
          <DataTable caption="Domain verifications" columns={domainColumns} data={domains.data?.items ?? []} isLoading={domains.isPending} error={domains.isError ? messageForError(domains.error) : undefined} getRowId={(r) => r.id} />
        </TabsContent>
        <TabsContent value="webhooks" className="pt-4">
          <DataTable caption="Billing webhook events" columns={webhookColumns} data={webhooks.data?.items ?? []} isLoading={webhooks.isPending} error={webhooks.isError ? messageForError(webhooks.error) : undefined} getRowId={(r) => r.id} />
        </TabsContent>
        <TabsContent value="payments" className="pt-4">
          <DataTable caption="Payments" columns={paymentColumns} data={payments.data?.items ?? []} isLoading={payments.isPending} error={payments.isError ? messageForError(payments.error) : undefined} getRowId={(r) => r.id} />
        </TabsContent>
        <TabsContent value="flags" className="pt-4">
          <FlagsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const Route = createFileRoute('/admin/operations')({
  component: OperationsPage,
});
